import { useState, useMemo } from "react";
import { Gavel, Gem, MessageCircle, Calendar, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

interface OnboardingProps {
  onComplete: () => void;
}

interface Slide {
  icon: React.ReactNode;
  title: string;
  description: string;
  bgGradient: string;
  accentColor: string;
}

const slides: Slide[] = [
  {
    icon: <Gavel className="w-16 h-16" />,
    title: "Welcome to Smash or Pass",
    description: "Discover amazing people near you and find your perfect match with our intuitive swipe experience.",
    bgGradient: "from-[hsl(190,80%,45%)] via-[hsl(175,65%,45%)] to-[hsl(265,50%,50%)]",
    accentColor: "hsl(190 80% 60%)",
  },
  {
    icon: <Gem className="w-16 h-16" />,
    title: "Swipe to Match",
    description: "Swipe right to like someone, left to pass. When you both swipe right, it's a match!",
    bgGradient: "from-[hsl(175,65%,40%)] via-[hsl(165,80%,45%)] to-[hsl(190,80%,50%)]",
    accentColor: "hsl(165 80% 55%)",
  },
  {
    icon: <MessageCircle className="w-16 h-16" />,
    title: "Start Conversations",
    description: "Once you match, break the ice and start chatting. Your next great connection is just a message away.",
    bgGradient: "from-[hsl(200,70%,45%)] via-[hsl(190,80%,50%)] to-[hsl(175,65%,45%)]",
    accentColor: "hsl(200 70% 60%)",
  },
  {
    icon: <Calendar className="w-16 h-16" />,
    title: "Smash or Pass",
    description: "Ready for spontaneous connections? Join Smash or Pass to find people available to meet today.",
    bgGradient: "from-[hsl(265,50%,50%)] via-[hsl(190,80%,45%)] to-[hsl(175,65%,45%)]",
    accentColor: "hsl(265 70% 70%)",
  },
];

interface FloatingShape {
  id: number;
  x: number;
  y: number;
  size: number;
  duration: number;
  delay: number;
  opacity: number;
}

const SlideBackground = ({ slideIndex }: { slideIndex: number }) => {
  const shapes = useMemo<FloatingShape[]>(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 60 + 30,
      duration: Math.random() * 10 + 8,
      delay: Math.random() * 5,
      opacity: Math.random() * 0.15 + 0.05,
    }));
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {shapes.map((shape) => (
        <div
          key={shape.id}
          className="absolute rounded-full animate-float"
          style={{
            left: `${shape.x}%`,
            top: `${shape.y}%`,
            width: shape.size,
            height: shape.size,
            opacity: shape.opacity,
            background: `radial-gradient(circle, hsl(0 0% 100% / 0.3), transparent)`,
            animationDuration: `${shape.duration}s`,
            animationDelay: `${shape.delay}s`,
          }}
        />
      ))}
    </div>
  );
};

export const Onboarding = ({ onComplete }: OnboardingProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide((prev) => prev + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    }
  };

  const handleComplete = () => {
    setIsExiting(true);
    setTimeout(onComplete, 300);
  };

  const slide = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;

  return (
    <div
      className={`fixed inset-0 z-40 flex flex-col transition-opacity duration-300 ${
        isExiting ? "opacity-0" : "opacity-100"
      }`}
    >
      {/* Full-bleed gradient background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${slide.bgGradient} transition-all duration-700`} />
      
      {/* Parallax floating shapes */}
      <SlideBackground slideIndex={currentSlide} />

      {/* Skip Button */}
      <div className="absolute top-4 right-4 z-10">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleComplete}
          className="text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10"
        >
          Skip
        </Button>
      </div>

      {/* Slide Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 relative z-10">
        {/* Icon with 3D floating effect */}
        <div
          className="relative mb-8 animate-float-card"
          key={`icon-${currentSlide}`}
        >
          <div className="absolute inset-0 rounded-full blur-2xl opacity-30"
            style={{ background: slide.accentColor }}
          />
          <div className="w-32 h-32 rounded-full glass flex items-center justify-center text-primary-foreground shadow-xl animate-scale-in"
            style={{ 
              background: `linear-gradient(135deg, hsl(0 0% 100% / 0.15), hsl(0 0% 100% / 0.05))`,
              boxShadow: `0 20px 60px -10px hsl(0 0% 0% / 0.3), 0 0 40px -10px ${slide.accentColor}` 
            }}
          >
            {slide.icon}
          </div>
        </div>

        {/* Title */}
        <h1
          className="text-3xl md:text-4xl font-extrabold text-primary-foreground text-center mb-4 animate-fade-in-up drop-shadow-lg"
          key={`title-${currentSlide}`}
          style={{ animationDelay: "0ms" }}
        >
          {slide.title}
        </h1>

        {/* Description */}
        <p
          className="text-primary-foreground/80 text-center max-w-md text-lg animate-fade-in-up leading-relaxed"
          key={`desc-${currentSlide}`}
          style={{ animationDelay: "200ms" }}
        >
          {slide.description}
        </p>
      </div>

      {/* Navigation */}
      <div className="pb-12 px-8 relative z-10">
        {/* Dots */}
        <div className="flex justify-center gap-3 mb-8" role="tablist" aria-label="Onboarding slides">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1} of ${slides.length}`}
              aria-selected={index === currentSlide}
              role="tab"
              className="min-w-[24px] min-h-[24px] flex items-center justify-center transition-all duration-300"
            >
              <span
                className={`block rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? "w-8 h-3 bg-primary-foreground shadow-lg"
                    : "w-3 h-3 bg-primary-foreground/30 hover:bg-primary-foreground/50"
                }`}
              />
            </button>
          ))}
        </div>

        {/* Buttons */}
        <div className="flex gap-4 max-w-md mx-auto">
          {currentSlide > 0 && (
            <Button
              variant="outline"
              size="lg"
              onClick={handlePrev}
              className="flex-1 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
          <Button
            size="lg"
            onClick={handleNext}
            className={`flex-1 spring-press border-0 shadow-xl ${
              isLastSlide 
                ? "bg-primary-foreground text-foreground hover:bg-primary-foreground/90 animate-glow-pulse" 
                : "bg-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/30 backdrop-blur-sm"
            }`}
          >
            {isLastSlide ? "Get Started" : "Next"}
            {!isLastSlide && <ChevronRight className="w-4 h-4 ml-2" />}
          </Button>
        </div>
      </div>
    </div>
  );
};
